import React, { CSSProperties, useState } from 'react';
import styled, { css } from 'styled-components';
import { BASIC_COLOURS } from '../../fundamentals/colours/variants';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { SHADOW_TYPES } from '../../fundamentals/shadows/shadows';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { TooltipArrow } from '../TooltipArrow/TooltipArrow';
import { ITooltipPositionProps, ITooltipProps, TooltipBubbleContainer, TooltipText, Arrow, POPPER_MODIFIERS } from './TooltipBase';
import { TooltipHelpIcon } from './TooltipHelpIcon';
import usePopper, { UsePopperState } from 'react-overlays/usePopper';
import { getPlacement, TOP } from '../../fundamentals/placements/placements';
import { BaseDiv } from '../../common/BaseElements';
import { IEoraThemeColours } from '../../fundamentals/colours/eoraColourUtils';

interface ITooltipBubbleProps
  extends ITooltipPositionProps,
    Pick<ITooltipProps, 'arrow' | 'light' | 'text' | 'supportingText' | 'micro'>,
    Pick<UsePopperState, 'styles' | 'placement'> {
  tooltipElement: React.RefObject<HTMLDivElement | null>;
  show: boolean;
}

export const tooltipColours = ({ eoraBaseColours, eoraCoreColours }: IEoraThemeColours) => ({
  tooltipColours: {
    [BASIC_COLOURS.BLACK]: {
      text: eoraBaseColours.VALUE.COLOR.BASE.WHITE,
      placeholder: eoraBaseColours.VALUE.COLOR.BASE.WHITE,
      background: eoraBaseColours.VALUE.COLOR.BASE.BLACK,
      caret: eoraCoreColours.OPACITIES.COLOR_DARK_OPACITY_75
    },
    [BASIC_COLOURS.WHITE]: {
      text: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_900,
      placeholder: eoraCoreColours.NEUTRAL.COLOR_NEUTRAL_900,
      background: eoraBaseColours.VALUE.COLOR.BASE.WHITE,
      caret: eoraCoreColours.OPACITIES.COLOR_LIGHT_OPACITY_75
    }
  }
});

const Trigger = styled(BaseDiv)<Pick<ITooltipProps, 'showHelpIcon'>>`
  position: relative;
  ${({ theme, showHelpIcon }) =>
    showHelpIcon &&
    css`
      height: ${theme.sizes[SIZES.MICRO]};
    `};
`;

const TooltipContainer = styled(BaseDiv)`
  ${useCenteredFlexCss};
`;

const TooltipBubble: React.FC<ITooltipBubbleProps> = ({ styles, placement, ...rest }) => {
  const { tooltipElement, show, text, light, supportingText, arrow } = rest;
  const { generateTestId } = useTestId(rest, 'Tooltip');

  if (!text) {
    return <></>;
  }

  return (
    <TooltipBubbleContainer
      {...generateTestId('Bubble')}
      {...rest}
      ref={tooltipElement}
      shadow={SHADOW_TYPES.STRONG}
      shadowAsFilter
      useFadeIn
      useFadeOut
      visible={show}
      style={styles.popper as CSSProperties}
    >
      {text &&
        (typeof text === 'string' ? (
          <TooltipText {...generateTestId('Text')} textEoraSize='MICRO' textStyle='SEMI_BOLD' light={light}>
            {text}
          </TooltipText>
        ) : (
          text
        ))}
      {supportingText && (
        <TooltipText {...generateTestId('SupportingText')} textEoraSize={'MICRO'} textStyle={'REGULAR'} light={light} alternate>
          {supportingText}
        </TooltipText>
      )}
      {arrow && (
        <Arrow placement={getPlacement(placement)} data-popper-arrow {...generateTestId('Arrow')} style={styles.arrow as CSSProperties}>
          <TooltipArrow placement={getPlacement(placement)} dark={!light} />
        </Arrow>
      )}
    </TooltipBubbleContainer>
  );
};

export const Tooltip: React.FC<ITooltipProps> = ({ arrow = true, onOpen, onClose, ...rest }) => {
  const { children, showHelpIcon, open } = rest;
  const { generateTestId } = useTestId(rest, 'Tooltip');
  const triggerElement = React.useRef<HTMLDivElement>(null);
  const tooltipElement = React.useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(open ?? false);

  const handleHover = (hovered: boolean) => {
    open === undefined && setShow(hovered);
    hovered && onOpen?.();
    !hovered && onClose?.();
  };

  const { styles, placement } = usePopper(triggerElement.current, tooltipElement.current, {
    placement: rest.placement ?? TOP,
    modifiers: POPPER_MODIFIERS
  });

  return (
    <TooltipContainer {...generateTestId()}>
      {showHelpIcon && <SpaceContainer gutterRight={4}>{children}</SpaceContainer>}
      <Trigger
        ref={triggerElement}
        {...generateTestId('Trigger')}
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        showHelpIcon={showHelpIcon}
      >
        {showHelpIcon ? <TooltipHelpIcon {...generateTestId('HelpIcon')} onFocus={handleHover} /> : children}
        <TooltipBubble placement={placement} show={show} arrow={arrow} tooltipElement={tooltipElement} {...rest} styles={styles} />
      </Trigger>
    </TooltipContainer>
  );
};
